import { AddressElement } from '@stripe/react-stripe-js'
import React, { useRef, useState } from 'react'
import { Button, Form, Grid, Input, Segment } from 'semantic-ui-react'
import * as R from 'ramda'

import { AllowPermission } from 'shared/components/AllowPermission'
import ErrorText from 'shared/components/forms/ErrorText'
import constants from 'config/constants'

import './index.css'

export const BillingDetailForm = ({
  companyName,
  onChange,
  defaultCustomer,
  onSubmit,
  loading,
  enableBillingTaxId,
  usBilled,
  taxIdMeta,
  taxIdValue,
  taxIdInlineError,
  taxIdChanged,
  addressCountryChanged,
  addressInlineError,
  onTaxIdChange,
  prompt,
}) => {
  const [isInvalid, setIsInvalid] = useState(Boolean(usBilled))
  const lastPropagatedValueRef = useRef(null)

  function removeNilOrEmptyValues(obj) {
    const isNilOrEmpty = R.either(R.isNil, R.isEmpty)
    return R.reject(isNilOrEmpty, obj)
  }

  const defaultValues = removeNilOrEmptyValues(defaultCustomer)
  const addressElementKey = JSON.stringify(defaultValues)

  return (
    <React.Fragment>
      {prompt}
      <h3>Billing details</h3>
      <Grid>
        <Grid.Column>
          <Segment>
            <Form
              onSubmit={(event) => {
                event.preventDefault()
                // Pass address-complete state up so the container can branch on
                // whether to fire the address PATCH (address-complete flow) or
                // skip it (tax-id-only flow).
                onSubmit(!isInvalid)
              }}
            >
              <Form.Group widths="2">
                <Form.Field>
                  <label htmlFor="billing-company-name">Company name</label>
                  <input
                    id="billing-company-name"
                    type="text"
                    name="company"
                    placeholder="Company"
                    value={companyName}
                    disabled
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor="billing-email">Billing email</label>
                  <input
                    id="billing-email"
                    type="email"
                    name="email"
                    placeholder="Billing Email"
                    value={defaultCustomer.email}
                    disabled
                  />
                </Form.Field>
              </Form.Group>

              <React.Fragment>
                <Form.Field error={Boolean(addressInlineError)}>
                  {/*
                   * The AddressElement input lives inside a cross-origin
                   * Stripe iframe, so Semantic UI's `.ui.form .field.error
                   * input` red-border + red-background rules can't reach
                   * the actual inputs. `ss-address-error-wrapper` (in the
                   * sibling index.css) replicates the theme's error-input
                   * palette on the wrapper div so the address error reads
                   * as one design with the EIN input's error state below.
                   */}
                  <div className={addressInlineError ? 'ss-address-error-wrapper' : ''}>
                    <AddressElement
                      key={addressElementKey}
                      options={{
                        mode: 'billing',
                        // @ts-ignore
                        defaultValues,
                      }}
                      onChange={(event) => {
                        // Propagate on ANY value change — including while the address is
                        // still incomplete — so in-progress edits (typing line 1 of a
                        // fresh address, clearing a field) are tracked as dirty. The
                        // container's baseline-compare decides dirty-vs-not, so hydration
                        // re-emits of the saved value settle back to clean. Dedupe on the
                        // serialized value so Stripe's non-content emits (focus/blur,
                        // validation-only ticks) don't trigger redundant re-renders.
                        //
                        // Replaces the earlier `event.complete || countryChanged` gate,
                        // which skipped every incomplete emit — so a fresh address whose
                        // line 1 was filled but not yet complete never marked dirty and
                        // the leave-guard didn't fire (PET-2084).
                        const serializedValue = JSON.stringify([addressElementKey, event.value])
                        if (serializedValue !== lastPropagatedValueRef.current) {
                          lastPropagatedValueRef.current = serializedValue
                          onChange(event.value)
                        }
                        setIsInvalid(!event.complete)
                      }}
                    />
                  </div>
                  {addressInlineError && (
                    <div role="alert">
                      <ErrorText>{addressInlineError}</ErrorText>
                    </div>
                  )}
                </Form.Field>

                {enableBillingTaxId && taxIdMeta && (
                  <Form.Field error={Boolean(taxIdInlineError)}>
                    <label htmlFor="billing-tax-id">{taxIdMeta.label}</label>
                    <Input
                      id="billing-tax-id"
                      placeholder={taxIdMeta.placeholder}
                      value={taxIdValue}
                      onChange={(event) => {
                        onTaxIdChange(event.target.value)
                      }}
                    />
                    {taxIdInlineError && (
                      <div role="alert">
                        <ErrorText>{taxIdInlineError}</ErrorText>
                      </div>
                    )}
                  </Form.Field>
                )}

                <AllowPermission permission={constants.permissions.manageBillingInformation}>
                  {/*
                   * Save unblocks when any of:
                   *   1. address is complete (default happy-path)
                   *   2. the tax ID has been edited (Option A — add/update
                   *      VAT/EIN without re-typing address)
                   *   3. the address country has changed (even if the new
                   *      address is not yet complete) — so a user changing
                   *      country can click Save and be told to finish the
                   *      address, instead of staring at a disabled button
                   *      with no explanation (bug fix, 2026-07-07).
                   * The onSubmit handler is the source of truth for
                   * validation — it fires a toast if the address is
                   * mid-country-change and incomplete.
                   */}
                  <Button
                    primary
                    type="submit"
                    disabled={
                      loading || (!usBilled && isInvalid && !taxIdChanged && !addressCountryChanged)
                    }
                    loading={loading}
                  >
                    Update billing details
                  </Button>
                </AllowPermission>
              </React.Fragment>
            </Form>
          </Segment>
        </Grid.Column>
      </Grid>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/Billing/BillingDetailForm/index.js